import { useEffect, useState } from "react";
import { X, Mic, BookOpen } from "lucide-react";
import EmojiPicker from "./EmojiPicker";
import AudioRecorder from "./AudioRecorder";
import ImageUpload from "./ImageUpload";
import VocabPicker from "./VocabPicker";
import VoiceInput from "./VoiceInput";
import { ttsLangFor } from "@/lib/i18n";

const COLOR_OPTIONS = [
  "#FFB627", "#FF6B47", "#FF8A5B", "#FFD23F",
  "#6FAE6D", "#5AA9E6", "#F38BA8", "#C58A6B",
  "#FFFFFF", "#FBEFD9",
];

export default function TileEditor({ open, initial, categories, onClose, onSave, lang = "en", profileId }) {
  const [label, setLabel] = useState("");
  const [speakText, setSpeakText] = useState("");
  const [emoji, setEmoji] = useState("🔤");
  const [imageUrl, setImageUrl] = useState("");
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [categoryId, setCategoryId] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);
  const [isGestalt, setIsGestalt] = useState(false);
  const [timeScope, setTimeScope] = useState("anytime");
  const [customAudio, setCustomAudio] = useState(null);
  const [customImage, setCustomImage] = useState(null);
  const [showVocab, setShowVocab] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel(initial?.label || "");
    setSpeakText(initial?.speak_text || "");
    setEmoji(initial?.emoji || "🔤");
    setImageUrl(initial?.image_url || "");
    setColor(initial?.color || (categories?.[0]?.color ?? COLOR_OPTIONS[0]));
    setCategoryId(initial?.category_id || categories?.[0]?.id || "");
    setIsFavorite(!!initial?.is_favorite);
    setIsGestalt(!!initial?.is_gestalt);
    setTimeScope(initial?.time_scope || "anytime");
    setCustomAudio(initial?.custom_audio_b64 || null);
    setCustomImage(initial?.custom_image_b64 || null);
  }, [open, initial, categories]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!label.trim() || !categoryId) return;
    onSave({
      label: label.trim(),
      speak_text: speakText.trim() || null,
      emoji,
      image_url: imageUrl.trim() || null,
      color,
      category_id: categoryId,
      is_favorite: isFavorite,
      is_gestalt: isGestalt,
      time_scope: timeScope,
      order: initial?.order ?? 0,
      custom_audio_b64: customAudio || null,
      custom_image_b64: customImage || null,
    });
  };

  return (
    <div className="modal-overlay" data-testid="tile-editor-modal">
      <form
        onSubmit={submit}
        className="brutal-card p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto tile-scroll"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="display text-2xl font-bold">
            {initial?.id ? "Edit tile" : "New tile"}
          </h2>
          <div className="flex gap-2">
            {!initial?.id && (
              <button
                type="button"
                className="pill-btn gold"
                onClick={() => setShowVocab(true)}
                data-testid="tile-editor-browse-vocab"
              >
                <BookOpen size={16} /> Browse 500+ words
              </button>
            )}
            <button type="button" className="pill-btn" onClick={onClose} data-testid="tile-editor-close">
              <X size={18} />
            </button>
          </div>
        </div>

        <label className="block mb-3">
          <span className="display font-semibold mb-1 block">Word or phrase</span>
          <input
            className="brutal-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Goldfish crackers, Hi Grandma!"
            required
            maxLength={120}
            data-testid="tile-label-input"
          />
          <div className="mt-2">
            <VoiceInput
              lang={ttsLangFor(lang)}
              onResult={(text) => {
                setLabel(text);
                if (!speakText) setSpeakText(text);
              }}
              data-testid="tile-label-voice"
            />
          </div>
        </label>

        <label className="block mb-4">
          <span className="display font-semibold mb-1 block">
            Speak as <span className="text-sm text-[color:var(--ink-soft)] font-normal">(optional, defaults to label)</span>
          </span>
          <input
            className="brutal-input"
            value={speakText}
            onChange={(e) => setSpeakText(e.target.value)}
            placeholder="What the device should say out loud"
            maxLength={120}
            data-testid="tile-speak-input"
          />
        </label>

        {/* Custom voice recording */}
        <div className="mb-4 brutal-card p-3 bg-[color:var(--paper-muted)]">
          <div className="flex items-center gap-2 mb-2">
            <Mic size={16} />
            <span className="display font-semibold">Your voice (optional)</span>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-2">
            Record yourself or your child saying this tile. Plays instead of the built-in voice.
          </p>
          <AudioRecorder value={customAudio} onChange={setCustomAudio} profileId={profileId} />
        </div>

        {/* Custom photo */}
        <div className="mb-4 brutal-card p-4 bg-[color:var(--gold)]" data-testid="tile-photo-card">
          <div className="display font-extrabold mb-1 text-base flex items-center gap-2">
            📷 Add a real photo
            <span className="text-[10px] uppercase tracking-[0.2em] bg-[color:var(--ink)] text-white px-1.5 py-0.5 rounded font-bold">recommended</span>
          </div>
          <p className="text-sm text-[color:var(--ink)] mb-3 leading-snug">
            <strong>This is the magic.</strong> Use Grandma's actual face, the real snack box,
            your kid's favorite toy. Recognition jumps because it's <em>their</em> world.
          </p>
          <ImageUpload value={customImage} onChange={setCustomImage} aiLabel={label} profileId={profileId} />
        </div>

        <div className="mb-3">
          <span className="display font-semibold mb-1 block">Symbol (used when no photo)</span>
          <EmojiPicker value={emoji} onChange={setEmoji} data-testid="tile-emoji-picker" />
        </div>

        <label className="block mb-3">
          <span className="display font-semibold mb-1 block">Category</span>
          <select
            className="brutal-input"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            required
            data-testid="tile-category-select"
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.emoji}  {c.name}</option>
            ))}
          </select>
        </label>

        <div className="mb-3">
          <span className="display font-semibold mb-1 block">Tile color</span>
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`w-10 h-10 rounded-full border-[3px] border-[color:var(--ink)] ${color === c ? "ring-4 ring-[color:var(--gold)]" : ""}`}
                style={{ background: c }}
                aria-label={`Color ${c}`}
                data-testid={`tile-color-${c}`}
              />
            ))}
          </div>
        </div>

        <label className="flex items-center gap-3 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isFavorite}
            onChange={(e) => setIsFavorite(e.target.checked)}
            className="w-5 h-5 accent-[color:var(--coral)]"
            data-testid="tile-favorite-checkbox"
          />
          <span className="display font-semibold">Show in Favorites</span>
        </label>

        <label className="flex items-start gap-3 mb-3 cursor-pointer brutal-card p-3 bg-[#F5F0FF]">
          <input
            type="checkbox"
            checked={isGestalt}
            onChange={(e) => setIsGestalt(e.target.checked)}
            className="w-5 h-5 mt-0.5 accent-[#A78BFA]"
            data-testid="tile-gestalt-checkbox"
          />
          <span className="block">
            <span className="display font-semibold flex items-center gap-1.5">
              <span aria-hidden>🗯️</span>
              Gestalt phrase (script)
            </span>
            <span className="block text-sm text-[color:var(--ink-soft)] mt-0.5">
              For gestalt language processors — the entire phrase is spoken
              as one unit, like "To infinity and beyond!" or "I just need a minute!"
            </span>
          </span>
        </label>

        <div className="mb-5 brutal-card p-3 bg-[#EAF6FF]">
          <div className="display font-semibold mb-1 flex items-center gap-1.5">
            <span aria-hidden>🏫</span>
            When this tile shows up
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-2 leading-snug">
            Hide distracting tiles during class hours, or keep classroom-only
            tiles out of evening/weekend boards. School-time runs Mon–Fri 8a–3p
            by default — adjust in Settings.
          </p>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Time scope">
            {[
              { v: "anytime",     label: "Anytime",       hint: "Always visible" },
              { v: "home_only",   label: "Home only",     hint: "Hides at school" },
              { v: "school_only", label: "School only",   hint: "Class context" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                role="radio"
                aria-checked={timeScope === opt.v}
                onClick={() => setTimeScope(opt.v)}
                className={`brutal-card p-2 text-left text-xs hover:bg-white transition ${timeScope === opt.v ? "ring-4 ring-[color:var(--gold)] bg-white" : "bg-[color:var(--paper)]"}`}
                data-testid={`tile-time-scope-${opt.v}`}
              >
                <div className="display font-bold">{opt.label}</div>
                <div className="text-[11px] text-[color:var(--ink-soft)]">{opt.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="pill-btn" onClick={onClose} data-testid="tile-editor-cancel">
            Cancel
          </button>
          <button type="submit" className="pill-btn primary" data-testid="tile-editor-save">
            Save
          </button>
        </div>
      </form>

      <VocabPicker
        open={showVocab}
        onClose={() => setShowVocab(false)}
        onPick={(w) => {
          setLabel(w.label);
          setEmoji(w.emoji);
          setShowVocab(false);
        }}
      />
    </div>
  );
}
